import { useCallback, useEffect, useState } from "react";
import api from "../../../services/api";

/**
 * Cadastro Mestre do módulo de Formação de Preço (CBS/IBS) — contas de
 * despesa e segmentos de receita do projeto atual.
 *
 * Ver design em RAVENA-DESK/documentacao-metodologias/Design_Tecnico_Formacao_Preco_v1.md
 *
 * `hasCadastroAvancado` (segmentos.length > 0) é o sinal que a tela
 * FormacaoPrecoTab usa para decidir entre modo simplificado (3 segmentos
 * hardcoded, como hoje) e modo avançado (N segmentos vindos do cadastro).
 */
export default function useFormacaoPreco() {
  const [contas, setContas] = useState([]);
  const [segmentos, setSegmentos] = useState([]);
  const [ecdContas, setEcdContas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const projectId = localStorage.getItem("vidal_selected_project");

  const reload = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [contasResp, segmentosResp, ecdResp] = await Promise.all([
        api.get(`/reforma/formacao-preco/contas/${projectId}`),
        api.get(`/reforma/formacao-preco/segmentos/${projectId}`),
        api.get(`/reforma/formacao-preco/contas/ecd/${projectId}`),
      ]);
      setContas(contasResp?.data?.data || []);
      setSegmentos(segmentosResp?.data?.data || []);
      setEcdContas(ecdResp?.data?.data || []);
    } catch (err) {
      console.error("[useFormacaoPreco] Erro ao carregar cadastro:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // ---- Contas de despesa ----
  const createConta = async (payload) => {
    const resp = await api.post(`/reforma/formacao-preco/contas/${projectId}`, payload);
    const novaConta = resp?.data?.data;
    setContas((prev) => [...prev, novaConta]);
    return novaConta;
  };

  const updateConta = async (contaId, patch) => {
    await api.put(`/reforma/formacao-preco/contas/${projectId}/${contaId}`, patch);
    setContas((prev) => prev.map((c) => (c.id === contaId ? { ...c, ...patch } : c)));
  };

  // Ata reunião: "importar a última ECD para o aplicativo, permitindo que o
  // usuário edite, adicione ou exclua contas de despesa diretamente".
  const importarEcd = async () => {
    const resp = await api.post(`/reforma/formacao-preco/contas/importar-ecd/${projectId}`);
    return resp.data; // { success, imported, skipped, total, message? }
  };

  const deleteConta = async (contaId) => {
    await api.delete(`/reforma/formacao-preco/contas/${projectId}/${contaId}`);
    setContas((prev) => prev.filter((c) => c.id !== contaId));
  };

  // ---- Segmentos ----
  const createSegmento = async (payload) => {
    const resp = await api.post(`/reforma/formacao-preco/segmentos/${projectId}`, payload);
    const novoSegmento = resp?.data?.data;
    setSegmentos((prev) => [...prev, novoSegmento]);
    return novoSegmento;
  };

  const updateSegmento = async (segmentoId, patch) => {
    await api.put(`/reforma/formacao-preco/segmentos/${projectId}/${segmentoId}`, patch);
    setSegmentos((prev) => prev.map((s) => (s.id === segmentoId ? { ...s, ...patch } : s)));
  };

  const deleteSegmento = async (segmentoId) => {
    await api.delete(`/reforma/formacao-preco/segmentos/${projectId}/${segmentoId}`);
    setSegmentos((prev) => prev.filter((s) => s.id !== segmentoId));
  };

  // ---- Percentuais (conta x segmento) ----
  const getPercentuais = async (segmentoId) => {
    const resp = await api.get(`/reforma/formacao-preco/percentuais/${projectId}/${segmentoId}`);
    return resp?.data?.data || [];
  };

  const savePercentuais = async (segmentoId, linhas) => {
    const resp = await api.put(`/reforma/formacao-preco/percentuais/${projectId}/${segmentoId}`, linhas);
    return resp?.data?.data || [];
  };

  // ---- Simulações (histórico de cenários) ----
  const listSimulacoes = async () => {
    const resp = await api.get(`/reforma/formacao-preco/simulacoes/${projectId}`);
    return resp?.data?.data || [];
  };

  const createSimulacao = async (payload) => {
    const resp = await api.post(`/reforma/formacao-preco/simulacoes/${projectId}`, payload);
    return resp?.data?.data || null;
  };

  const deleteSimulacao = async (simulacaoId) => {
    await api.delete(`/reforma/formacao-preco/simulacoes/${projectId}/${simulacaoId}`);
  };

  return {
    projectId,
    contas,
    segmentos,
    ecdContas,
    loading,
    error,
    hasCadastroAvancado: segmentos.length > 0,
    hasEcd: ecdContas.length > 0,
    reload,
    createConta,
    updateConta,
    deleteConta,
    importarEcd,
    createSegmento,
    updateSegmento,
    deleteSegmento,
    getPercentuais,
    savePercentuais,
    listSimulacoes,
    createSimulacao,
    deleteSimulacao,
  };
}
